import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import CheckIcon from '@mui/icons-material/Check'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import SearchIcon from '@mui/icons-material/Search'
import {
  Box,
  ClickAwayListener,
  InputAdornment,
  Paper,
  Popper,
  TextField,
  Typography,
} from '@mui/material'

import { BASE_URL_WITH_REACT } from '~config'
import { RootState } from '~redux/rootReducer'
import { useGetIdentityQuery, useListTagsQuery } from '~redux/slices/apiSlice'
import { TagData, TagId } from '~types'

import Tag from './Tag'

type TagSelectorProps = {
  selectedTags: TagData[]
  onChange: (selectedTags: TagData[]) => void
  placeholder?: string
  selectedIdentityId?: string
  style?: React.CSSProperties
  hasBorder?: boolean
  tagSize?: 'small' | 'medium'
  addTag?: (tag: TagData) => void
  removeTag?: (tag: TagData) => void
  calculatedTags?: TagId[]
}

const TagSelector: React.FC<TagSelectorProps> = ({
  selectedTags,
  onChange,
  placeholder,
  selectedIdentityId,
  style,
  hasBorder = true,
  tagSize = 'small',
  addTag,
  removeTag,
  calculatedTags,
}) => {
  const userId = useSelector((state: RootState) => state?.auth.userId)

  const [searchQuery, setSearchQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [focusedIndex, setFocusedIndex] = useState<number>(-1)
  const anchorRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  const { data: listTagsResponse } = useListTagsQuery(
    {
      user_id: userId as string,
      owner_id: [selectedIdentityId],
    },
    { skip: !userId },
  )

  const { data: getIdentityResponse } = useGetIdentityQuery(
    {
      user_id: userId as string,
      identity_id: selectedIdentityId as string,
    },
    { skip: !userId || !selectedIdentityId },
  )
  const organizationHandle = getIdentityResponse?.identity.handle.replace(/_/g, '-')

  const handleTagToggle = (tagToToggle: TagData): void => {
    const isCurrentlySelected = selectedTags.some((tag) => tag.id === tagToToggle.id)
    let newSelectedTags: TagData[]

    if (isCurrentlySelected) {
      if (removeTag) {
        removeTag(tagToToggle)
      }
      newSelectedTags = selectedTags.filter((tag) => tag.id !== tagToToggle.id)
    } else {
      if (addTag) {
        addTag(tagToToggle)
      }
      newSelectedTags = [...selectedTags, tagToToggle]
    }

    onChange(newSelectedTags)
  }

  const filterAndSortTags = (tags: TagData[], query: string): TagData[] => {
    const lowercaseQuery = query.toLowerCase()
    const selectedTagIds = new Set(selectedTags.map((tag) => tag.id))

    return tags
      .filter((tag) => tag.name.toLowerCase().includes(lowercaseQuery))
      .sort((a, b) => {
        if (selectedTagIds.has(a.id) && !selectedTagIds.has(b.id)) return -1
        if (!selectedTagIds.has(a.id) && selectedTagIds.has(b.id)) return 1

        const aStartsWithQuery = a.name.toLowerCase().startsWith(lowercaseQuery)
        const bStartsWithQuery = b.name.toLowerCase().startsWith(lowercaseQuery)
        if (aStartsWithQuery && !bStartsWithQuery) return -1
        if (!aStartsWithQuery && bStartsWithQuery) return 1

        return a.name.localeCompare(b.name)
      })
  }
  const filteredTags = filterAndSortTags(listTagsResponse?.tags ?? [], searchQuery)

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchQuery(event.target.value)
  }

  const handleToggle = (): void => {
    setIsOpen(!isOpen)
    if (!isOpen) {
      setFocusedIndex(-1)
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus()
        }
      }, 0)
    }
  }

  const handleClose = (): void => {
    setIsOpen(false)
    setSearchQuery('')
    setFocusedIndex(-1)
  }

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleToggle()
    }
  }

  const handleOptionKeyDown = (
    e: React.KeyboardEvent,
    index: number,
    newContactRow: boolean,
    tag?: TagData,
  ): void => {
    e.stopPropagation()
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault()
        if (!newContactRow && tag) {
          handleTagToggle(tag)
        } else if (newContactRow) {
          window.open(
            `${BASE_URL_WITH_REACT}/organizations/${organizationHandle}/settings/tags`,
            '_blank',
          )
        }
        break
      case 'ArrowDown':
        e.preventDefault()
        setFocusedIndex(Math.min(index + 1, filteredTags.length))
        break
      case 'ArrowUp':
        e.preventDefault()
        setFocusedIndex(Math.max(index - 1, 0))
        break
      case 'Tab':
        if (!e.shiftKey && index === filteredTags.length) {
          e.preventDefault()
          setFocusedIndex(0)
        } else if (e.shiftKey && index === 0) {
          e.preventDefault()
          setFocusedIndex(filteredTags.length)
        }
        break
      case 'Escape':
        handleClose()
        break
    }
  }

  useEffect(() => {
    if (!isOpen) {
      setFocusedIndex(-1)
    }
  }, [isOpen])

  const TagDropdownRow: React.FC<{
    index: number
    children: React.ReactNode
    newContactRow: boolean
    tag?: TagData
  }> = ({ index, children, newContactRow, tag }) => {
    return (
      <Box
        key={`${tag?.id}-${index}`}
        tabIndex={isOpen ? 0 : -1}
        onClick={() => {
          if (!newContactRow && tag) {
            handleTagToggle(tag)
          } else if (newContactRow) {
            window.open(
              `${BASE_URL_WITH_REACT}/organizations/${organizationHandle}/settings/tags`,
              '_blank',
            )
          }
        }}
        onKeyDown={(e) => handleOptionKeyDown(e, index, newContactRow, tag)}
        ref={index === focusedIndex ? (el: HTMLDivElement | null): void => el?.focus() : undefined}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1,
          cursor: 'pointer',
          '&:hover': { backgroundColor: 'action.hover' },
          backgroundColor: selectedTags.some((selectedTag) => selectedTag.id === tag?.id)
            ? '#E1F2F9'
            : 'inherit',
          '&:focus': { backgroundColor: 'action.hover' },
        }}
      >
        {children}
        {selectedTags.some((selectedTag) => selectedTag.id === tag?.id) && (
          <CheckIcon sx={{ color: 'success.main' }} />
        )}
      </Box>
    )
  }

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box sx={{ position: 'relative', width: '250px', ...style }}>
        <Box
          ref={anchorRef}
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
          tabIndex={0}
          sx={{
            backgroundColor: '#fff',
            border: hasBorder ? '1px solid #ccc' : 'none',
            borderRadius: 1,
            p: 1,
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            '&:hover': { borderColor: '#000' },
            '&:focus-visible': {
              border: '2px solid #ffffff',
              outline: '2px solid #0894cf',
              boxShadow: '0 0 0 2px #98e0ff, 0 0 0 4px #98e0ff',
            },
          }}
        >
          <Box sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {selectedTags.length === 0 ? (
              <Typography color='text.secondary'>{placeholder || 'Select tags...'}</Typography>
            ) : (
              selectedTags.map((tag) => (
                <Tag
                  key={tag.id}
                  tag={tag.name}
                  color={tag.color}
                  size={tagSize}
                  onDelete={() => handleTagToggle(tag)}
                  calculated={calculatedTags?.includes(tag.id as TagId) ?? false}
                />
              ))
            )}
          </Box>
          <ExpandMoreIcon
            sx={{
              transition: 'transform 0.3s',
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        </Box>
        <Popper
          open={isOpen}
          anchorEl={anchorRef.current}
          placement='bottom-start'
          style={{ width: anchorRef.current?.clientWidth, zIndex: 1300 }}
        >
          <Paper sx={{ mt: 1, p: 2 }}>
            <TextField
              inputRef={searchInputRef}
              fullWidth
              size='small'
              placeholder='Search tags...'
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyDown={(e) => e.stopPropagation()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ mt: 2, maxHeight: 200, overflow: 'auto' }}>
              {selectedIdentityId && (
                <TagDropdownRow index={0} newContactRow={true}>
                  <Typography color='primary'> + Create new tag</Typography>
                </TagDropdownRow>
              )}
              {filteredTags.map((tag, index) => (
                <TagDropdownRow
                  key={`row-${tag.id}-${index}`}
                  tag={tag}
                  index={index + 1}
                  newContactRow={false}
                >
                  <Tag tag={tag.name} color={tag.color} />
                </TagDropdownRow>
              ))}
            </Box>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  )
}

export default TagSelector
